import React, { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { PageHero } from "../components/PageHero";
import { CTABand } from "../components/CTABand";
import { Reveal } from "../components/Reveal";
import { WordReveal } from "../components/WordReveal";
import { WhatsAppLine } from "../components/WhatsAppLine";
import { Link } from "react-router-dom";
import { ArrowRightIcon } from "lucide-react";
import { MapleWatermark } from "../components/MapleLeaf";
import { ParallaxImage } from "../components/ParallaxImage";
import { IMAGES, MCC_PHOTOS } from "../data/images";
const ACTIVITIES = [{
  verb: "Move",
  detail: "Basketball · Soccer · Track and field · Fitness"
}, {
  verb: "Speak",
  detail: "Speech and debate clubs"
}, {
  verb: "Build",
  detail: "Entrepreneurship Club"
}, {
  verb: "Create",
  detail: "Art and theatre clubs · Media Club"
}, {
  verb: "Lead",
  detail: "Community leadership and service"
}];
function Collage() {
  const ref = useRef<HTMLDivElement>(null);
  const reduce = useReducedMotion();
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });
  const slow = useTransform(scrollYProgress, [0, 1], reduce ? [0, 0] : [60, -60]);
  const fast = useTransform(scrollYProgress, [0, 1], reduce ? [0, 0] : [160, -160]);
  const mid = useTransform(scrollYProgress, [0, 1], reduce ? [0, 0] : [100, -40]);
  return <div ref={ref} className="relative mx-auto grid max-w-[1440px] grid-cols-12 gap-4 px-5 py-20 md:px-9 md:py-28" data-id="element-1669" data-mp="o79zon8wrgkr">
      <motion.figure style={{
      y: slow
    }} className="col-span-12 md:col-span-6" data-id="element-1670" data-mp="j98he4jb3lac">
        <img src={IMAGES.lab} alt="Students in a science laboratory" loading="lazy" className="aspect-[4/5] w-full object-cover" data-id="element-1671" data-mp="wzlqia7xp619" />
        <figcaption className="mt-3 text-xs text-[#6b7378]" data-id="element-1672" data-mp="s5zbwci7ms2t">Laboratories</figcaption>
      </motion.figure>
      <motion.figure style={{
      y: fast
    }} className="col-span-7 mt-10 md:col-span-4 md:col-start-8 md:mt-40" data-id="element-1673" data-mp="4g4xhqi8svgg">
        <img src={IMAGES.ict} alt="Students coding in the ICT lab" loading="lazy" className="aspect-[3/4] w-full object-cover" data-id="element-1674" data-mp="1zlnvb1whumj9" />
        <figcaption className="mt-3 text-xs text-[#6b7378]" data-id="element-1675" data-mp="24jz55cmsgx1h">ICT & technology</figcaption>
      </motion.figure>
      <motion.figure style={{
      y: mid
    }} className="col-span-5 col-start-8 -mt-20 md:col-span-3 md:col-start-5 md:-mt-32" data-id="element-1676" data-mp="29ciiq9lh7s6a">
        <img src={IMAGES.library} alt="Students studying in the library" loading="lazy" className="aspect-square w-full object-cover" data-id="element-1677" data-mp="2ea0ppufxnxbn" />
        <figcaption className="mt-3 text-xs text-[#6b7378]" data-id="element-1678" data-mp="1llwmdfku3wc4">Library</figcaption>
      </motion.figure>
    </div>;
}
export function StudentLife() {
  return <>
      <PageHero variant="editorial" eyebrow="EXTRA-CURRICULAR ACTIVITIES" title="Learning doesn’t stop at the classroom." description="A vibrant community, a well-rounded education, and real leadership roles." image={IMAGES.life} imageAlt="Students collaborating in a technology space" breadcrumb="Extra-Curricular Activities" data-id="element-1679" data-mp="1qjocq55tzae7" />

      <main data-id="element-1680" data-mp="vbw3mkscc78o">
        <Collage data-id="element-1681" data-mp="vcdzvhf06lgl" />

        <section className="bg-[#061f38] py-24 text-white md:py-32" data-id="element-1682" data-mp="vaugvj1ypwb4">
          <div className="mx-auto max-w-[1440px] px-5 md:px-9" data-id="element-1683" data-mp="qd1ez27ozogn">
            <p className="text-xs font-semibold tracking-[0.18em] text-[#f0b1b9]" data-id="element-1684" data-mp="qevzwgiyrgkq">BEYOND THE TIMETABLE</p>
            <ul className="mt-10 border-t border-white/20" data-id="element-1685" data-mp="ljztucgw6som">
              {ACTIVITIES.map((a, i) => <li key={a.verb} className="group grid items-baseline gap-2 border-b border-white/20 py-6 md:grid-cols-[80px_1fr_1fr] md:py-8" data-id="element-1686" data-mp="gl5ddh6edg7o" data-mpi={i}>
                  <span className="text-xs text-[#f0b1b9]" data-id="element-1687" data-mp="7m1r64bxokoy">0{i + 1}</span>
                  <WordReveal as="h3" text={a.verb} className="text-[clamp(3.5rem,9vw,8rem)] font-medium leading-[0.85] tracking-[-0.08em]" data-id="element-1688" data-mp="2mymikzv8ghi" />
                  <Reveal delay={0.15} data-id="element-1689" data-mp="3tut9an9awki0"><p className="text-base text-white/65 md:text-right" data-id="element-1690" data-mp="3tvclrog6mgon">{a.detail}</p></Reveal>
                </li>)}
            </ul>
          </div>
        </section>

        <section className="mx-auto max-w-[1440px] px-5 py-24 md:px-9 md:py-32" data-id="element-1691" data-mp="1s3g5k1j2kinj">
          <div className="grid gap-10 lg:grid-cols-12 lg:items-end" data-id="element-1692" data-mp="pps4vw9mwf81">
            <Reveal className="lg:col-span-5" data-id="element-1693" data-mp="tq2a36plr1lu">
              <p className="text-xs font-semibold tracking-[0.18em] text-[#bb1e35]" data-id="element-1694" data-mp="tpshdbd6agcg">BETWEEN CLASSES</p>
              <h2 className="mt-5 text-4xl font-medium leading-[0.95] tracking-[-0.06em] md:text-6xl" data-id="element-1695" data-mp="fq17nmtovm49">Room to unwind.</h2>
              <p className="mt-6 max-w-sm text-base leading-7 text-[#4f5963]" data-id="element-1696" data-mp="krqj33ktq1sv">The student lounge: chess, board games and a wall painted by the Class of 2021, The Inexorables, with every student’s handprint.</p>
            </Reveal>
            <div className="grid grid-cols-5 gap-3 lg:col-span-7" data-id="element-1697" data-mp="3t5pd3zo8jd3q">
              <ParallaxImage src={MCC_PHOTOS.inexorablesLounge} alt="MCC student lounge with The Inexorables mural and chess table" className="col-span-3 aspect-[4/5]" reveal="left" data-id="element-1698" data-mp="3t3cs1dn6kmmg" />
              <ParallaxImage src={MCC_PHOTOS.commonRoom} alt="MCC common room with seating, chess table and fitness equipment" className="col-span-2 mt-16 aspect-[3/4]" reveal="right" data-id="element-1699" data-mp="3j81jxe6a2dno" />
            </div>
          </div>
        </section>

        <section className="relative overflow-hidden bg-[#c5233d] text-white" data-id="element-1700" data-mp="30j8xnrjwp9gp">
          <MapleWatermark className="-right-16 -top-24 h-[28rem] w-[28rem]" tone="text-white/[0.1]" data-id="element-1701" data-mp="30lbxf3av2gym" />
          <Link to="/student-life/sports-fiesta" className="group relative mx-auto grid max-w-[1440px] items-center gap-6 px-5 py-14 md:grid-cols-[1fr_auto] md:px-9 md:py-20" data-id="element-1702" data-mp="3ege1dkw5dw8s">
            <div data-id="element-1703" data-mp="3nhlyhzmxgr1a">
              <p className="text-xs font-semibold tracking-[0.18em] text-white/75" data-id="element-1704" data-mp="3nh2dub5w7ojk">THE BIGGEST DAY OF THE YEAR</p>
              <p className="mt-3 text-5xl font-medium tracking-[-0.07em] md:text-7xl" data-id="element-1705" data-mp="13udr87zozig">Sports Fiesta</p>
            </div>
            <span className="grid h-16 w-16 place-items-center bg-white text-[#c5233d] transition-transform duration-200 group-hover:translate-x-1" data-id="element-1706" data-mp="3s9n8tg0jc7oo"><ArrowRightIcon size={22} data-id="element-1707" data-mp="b2jmaadzx7m1" /></span>
          </Link>
        </section>

        <section className="mx-auto grid max-w-[1440px] gap-10 px-5 py-28 md:px-9 lg:grid-cols-12 lg:py-36" data-id="element-1708" data-mp="63zzk7rkvrsm">
          <div className="lg:col-span-7" data-id="element-1709" data-mp="d7g7yk3s8f6o">
            <WordReveal as="p" text="Have a talent the clubs don’t cover yet? Students can propose their own club to the principal at the start of each school year." className="text-3xl font-medium leading-[1.12] tracking-[-0.045em] md:text-5xl" data-id="element-1710" data-mp="i5811hg5y7cg" />
          </div>
          <Reveal delay={0.2} className="flex flex-col justify-end lg:col-span-4 lg:col-start-9" data-id="element-1711" data-mp="rwm5m3mz1xcg">
            <p className="text-sm leading-6 text-[#4f5963]" data-id="element-1712" data-mp="wtkyyjyta09l">Activities are drawn from MCC’s published extracurricular list and may vary by school year.</p>
            <div className="mt-6" data-id="element-1713" data-mp="15uat7j8fbil7"><WhatsAppLine label="Ask about this year’s activities" context="student life and extracurricular activities at MCC" data-id="element-1714" data-mp="15vu9h9u2xtmz" /></div>
          </Reveal>
        </section>
      </main>

      <CTABand title="Come and see student life for yourself." image={IMAGES.life} data-id="element-1715" data-mp="37mpb4iaywn1m" />
    </>;
}